import {Box} from "@mui/material";
import {Theme} from "@mui/material/styles";
import React from "react";
import {Category, CategoryDetails, CategoryPoint, SubCategory} from "@/types";
import ContentTitles from "@/ui/content-titles/content-titles";
import {BUSINESS_NAME} from "@/content";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import ContactForm from "@/ui/contact-form/contact-form";
import {useResizeHook} from "@/lib";
import CategoryHighlightsGallery from "@/ui/category-highlights/category-highlights-gallery";

type CategoryHighlightsContentTypes = {
    category: CategoryDetails & Category;
    showContactsButton?: boolean;
    showPoints?: boolean;
    hideSubtitle?: boolean;
    hideServicesLinks?: boolean;
    alternateBackground?: boolean;
    showMainCategoryLink?: boolean;
}
const CategoryHighlightsContent = ({
                                       category,
                                       hideSubtitle,
                                       showContactsButton,
                                       showPoints,
                                       hideServicesLinks,
                                       alternateBackground,
                                       showMainCategoryLink
                                   }: CategoryHighlightsContentTypes) => {

    const {lessThanTablet} = useResizeHook();

    const buttonStyles = (theme: Theme) => ({
        textTransform: 'capitalize',
        border: `1px solid ${alternateBackground || lessThanTablet ? theme.palette.primary.main : theme.palette.secondary.main}`,
        color: alternateBackground || lessThanTablet ? theme.palette.primary.main : theme.palette.secondary.main,
        borderRadius: '10px',
        textAlign: 'center',
        backgroundColor: '#FFF',
    })

    const buttonMainStyle = (theme: Theme) => ({
        textTransform: 'capitalize',
        color: theme.palette.text.primary,
        backgroundColor: theme.palette.background.paper,
        mt: 3,
        '& ::hover': {
            color: theme.palette.text.primary,
            backgroundColor: theme.palette.background.paper,
        },
        borderRadius: '10px',
    })


    return (<Box
        px={4}
        sx={(theme) => ({
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            alignContent: 'center',
            py: 5,
            [theme.breakpoints.down('lg')]: {
                px: 0,
                py: 2,
            }
        })}
    >


        <ContentTitles
            overline={`${BUSINESS_NAME} ${category.categoryType}`}
            title={category.name}
            titleVariant="h3"
        />
        <Box sx={(theme) => ({
            [theme.breakpoints.up('lg')]: {
                display: 'none',
            }
        })}>
            {lessThanTablet ? <img src={category.images?.[0]?.original} alt={category.images?.[0]?.thumbnailTitle}/> :
                <CategoryHighlightsGallery category={category}/>}
        </Box>
        <ContentTitles
            subTitle={hideSubtitle ? '' : Array.isArray(category.description) ? category.description.join(', ') : category.description || ''}
        />

        {
            (!!category.subCategories?.length && !hideServicesLinks && !lessThanTablet) &&
            (
                <Box sx={(theme: Theme) => ({
                    display: 'flex',
                    gap: '10px',
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    [theme.breakpoints.down('lg')]: {
                        pt: 3,
                        pb: 2,
                    },
                })}
                >
                    {category.subCategories?.map((sc: SubCategory) => (
                            <div key={sc.id}>
                                <Button
                                    variant='outlined'
                                    // className='scale-in'
                                    sx={(theme: Theme) => buttonStyles(theme)}
                                    href={`/${category.categoryType}/${category.slug}/${sc.slug}`}
                                >
                                    {sc.title}
                                </Button>
                            </div>
                        )
                    )}
                </Box>
            )
        }
        {showPoints && !!category.points?.length && (
            <div>
                <Box
                    sx={(theme: Theme) => ({
                        pt: 5,
                        pb: 3,
                        [theme.breakpoints.down('md')]: {
                            py: 0,
                        },
                    })}
                >
                    {category.points?.map((point: CategoryPoint, index) => (
                        <Box key={index} sx={(theme: Theme) => ({
                            display: 'flex',
                            alignItems: 'center',
                            pb: 2,
                            justifyContent: 'center',
                            [theme.breakpoints.down('md')]: {
                                alignItems: 'flex-start',
                            },
                        })}>
                            {!!point.icon && point.icon}
                            <Box ml={1}>
                                <Typography variant='body2' component='h6'
                                            sx={(theme: Theme) => ({
                                                [theme.breakpoints.down('md')]: {
                                                    py: 0,
                                                    my: 0,
                                                },
                                            })} align='left'

                                >
                                    {point.title}
                                </Typography>
                            </Box>
                        </Box>
                    ))}
                </Box>
            </div>
        )}
        {showContactsButton && (
            <div>
                <Box
                    sx={(theme: Theme) => ({
                        mb: 2,
                        mt: 4,
                        [theme.breakpoints.down('md')]: {
                            mb: 2,
                            mt: 1,
                        },
                    })}

                >
                    <ContactForm
                        buttonMessage={`Contact Us about ${category.name}`}
                        buttonVariant={'contained'}
                    />
                </Box>
            </div>
        )}
        {
            showMainCategoryLink && (
                <Button
                    variant='outlined'
                    // className='slide-up'
                    sx={(theme: Theme) => buttonMainStyle(theme)}
                    href={`/${category.categoryType}/${category.slug}`}
                >
                    {category.name}
                </Button>
            )
        }
    </Box>)
}

export default CategoryHighlightsContent;
